<template>
  <div class='content'>
    <p v-if="!showCreateEnvelope && isFromDashboard" :endpoint="api.$.evedpaycard.envelopes"><br /><br /><br /></p>
    <grid v-if='!showCreateEnvelope'
          v-show="!isFromDashboard"
          :name='$t("EvedPay Cards")'
          desc='Manage accounts, cards and transactions'
          search=true
          search-placeholder='My Envelopes'
          resizing=false
          :endpoint='api.$.evedpaycard.envelopes'
          :sort="sort"
          :row-classes="rowClasses"
          ref="grid"
          fit-columns
          legacy
          footer
          totals
          :tabs='tabs'
          alias="EvedPayCards"
          @selectedRows="rowSelected"
          :no-tooltip-fields="noTooltipFields"
          :filter="{}"
    >
      <span slot="additionalButtons">
        <ed-button class="btn btn-actionbar"
                   v-if="showApproveButton"
                   :disabled="envelopeActionDisabled('approve')"
                   v-tooltip="actionDisabledReason('approve')"
                   @success="refresh"
                   @click="showApproveDialog = true">
          Approve
        </ed-button>
        <RejectEnvelope v-if="showRejectButton"
                        :disabled="envelopeActionDisabled('reject')"
                        v-tooltip="actionDisabledReason('reject')"
                        :envelope="envelope"
                        :selected-rows="selectedRows"
                        @success="refresh" />
        <ed-button button-class="btn btn-actionbar"
          @click="createAPFile"
          :disabled="envelopeActionDisabled('createAPFile')"
          v-tooltip="actionDisabledReason('createAPFile')">
          CREATE AP FILE
        </ed-button>       
        <ed-button button-class="btn btn-actionbar"
          @click="exportEnvelope"
          :disabled="envelopeActionDisabled('exportEnvelope')"
          v-tooltip="actionDisabledReason('exportEnvelope')">
          EXPORT ENVELOPE
        </ed-button>
        <ed-button button-class="btn btn-actionbar"
          @click="pushToAccountingSystem"
          :disabled="loadingAccountSettings || envelopeActionDisabled('pushToAccountingSystemAction')"
          v-tooltip="loadingAccountSettings || actionDisabledReason('pushToAccountingSystemAction')">
          PUSH TO PSL+
        </ed-button>
        <AddTransactionNumber :selected-rows="selectedRows"
                              :disabled="envelopeActionDisabled('addTransactionNumber')"
                              v-tooltip="actionDisabledReason('addTransactionNumber')"
                              @success="refresh"/>
      </span>
      <span slot="additionalLabel" class="form-group no-margin">
        <i class="fas fa-exclamation"></i>
        <span>&nbsp;</span>
        <label>Click column headers to apply filters</label>
      </span>
    </grid>
    <CreateEnvelope title="CREATE ENVELOPE"
                    :editEnvelope="true"
                    :envelope="envelope"
                    v-if="showCreateEnvelope"
                    @success="showCreateEnvelope=!showCreateEnvelope; updateUrlBackToEnvelopesPage(); envelope = {}"
                    @backToTransactions="showCreateEnvelope=!showCreateEnvelope; updateUrlBackToEnvelopesPage(); envelope = {}" />
    <ed-modal title="Approval Needed" :show="showApprovalUsers" @close="showApprovalUsers = false" primary-button="Close" secondary-button="false">
      <ApprovalNeededUsers slot="body" :approvalUsersData="approvalUsersData" />
    </ed-modal>
    <ApproveDialog :showModal="showApproveDialog"
                   :list="transformRows(selectedRows)"
                   :programLabel="programLabel"
                   :loading="isApproving"
                   envelopeApproval
                   @approve="doApprove"
                   @close="() => { showApproveDialog = false }" />
  </div>
</template>

<script>
import Grid from '@/components/Grid'
import model from './model.js'
import AddTransactionNumber from './AddTransactionNumber.vue'
import ApproveDialog from '@/components/_modules/Dashboard/widgets/ApprovalNeeded/approve-dialog'
import RejectEnvelope from './RejectEnvelope.vue'
import ApprovalNeededUsers from '@/components/_modules/Programs/Card-list/ApprovalNeededUsers.vue'
import tabs from '@/components/_modules/Programs/Card-envelopes/tabs'
import CreateEnvelope from '@/components/_modules/Programs/Card-transactions/CreateEnvelope.vue'

import { EventBus } from '@/event-bus'
import events from '@/core/events/events'
// import { backToMap } from '@/components/Layouts/navless'
const backToMap = {
  'EnvelopeApprovalDashboard': '/ApprovalDashboard'
}

const cancelTooltip = 'Please select an envelope.'

// reload page after browser back pressed
function handlePopState(event) {
  if (document.location.href.includes('envelopes')) {
    window.location.reload()
  }
}
window.addEventListener('popstate', handlePopState)

export default {
  components: {
    Grid,
    AddTransactionNumber,
    ApprovalNeededUsers,
    ApproveDialog,
    RejectEnvelope,
    CreateEnvelope
  },
  data() {
    return {
      model,
      sort: { CreatedDate: 'desc' },
      tabs: tabs.tabs,
      selectedAll: null,
      selectedRows: [],
      showApprovalUsers: false,
      approvalUsersData: {},
      showApproveDialog: false,
      isApproving: false,
      showCreateEnvelope: false,
      creatingAPFile: false,
      pushingToAccountingSystem: false,
      accountSettings: [],
      loadingAccountSettings: false,
      envelope: {},
      highlightedEnvelopeId: '',
      rowClasses: {
        'row-highlight': params => {
          return (params.data && params.data.Id) === this.highlightedEnvelopeId
        }
      },
      noTooltipFields: ['EnvelopeNumber', 'EnvelopeStatus'],
      waitingApprovalStatuses: ['Open - Waiting Approval', 'Exported - Waiting Approval', 'Posted - Waiting Approval'],
      rejectedStatuses: ['Rejected', 'Declined', 'Exported - Rejected', 'Posted - Rejected', 'Exported - Declined', 'Posted - Declined']
    }
  },

  async created() {
    this.programName = this.singularize(this.$root.getUser.settings.Labels.ProgramLabel)
    this.model.ProgramName.label = this.programName

    if (!this.$root.getUser.settings.HasBusinessUnit) {
      delete this.model.BusinessUnit
    }

    EventBus.$off(events.EVEDPAY_CARDS.OPEN_ENVELOPE)
    EventBus.$on(events.EVEDPAY_CARDS.OPEN_ENVELOPE, envelope => {
      this.envelope = envelope.data
      this.showCreateEnvelope = true
    })

    if (this.$route.query.EnvelopeId) {
      let envelope = await this.getEnvelope(this.$route.query.EnvelopeId, this.isFromDashboard)
      if (this.isFromDashboard) {
        envelope.FromDashboard = true
      }
      await EventBus.$emit(events.EVEDPAY_CARDS.OPEN_ENVELOPE, envelope)
    }

    this.highlightedEnvelopeId = localStorage.getItem('row-highlight')
  },

  activated() {
    this.showCreateEnvelope = false
    this.envelope = {}
    this.highlightedEnvelopeId = ''
    localStorage.setItem('row-highlight', '')
    this.redrawRows()
  },

  computed: {
    isFromDashboard() {
      return this.$route.params.page === 'EnvelopeApprovalDashboard' || this.$route.query.fromDashboard === 'true'
    },
    showApproveButton() {
      return this.isUserInApprovalList()
    },
    showRejectButton() {
      return this.isUserInApprovalList()
    }
  },

  watch: {
    showCreateEnvelope(newValue) {
      if (newValue) {
        this.highlightedEnvelopeId = this.envelope.Id
      }
    }
  },

  methods: {
    async approveOrReject(envelopes) {
      this.isApproving = true
      try {
        let payload = {
          envelopesData: envelopes
        }
        const response = await this.api.post(this.api.$.evedpaycard.approveOrRejectEnvelope, payload)
        if (!response.data) {
          throw new Error('Error during the request')
        }
        this.showApproveDialog = false
        this.refresh()
      } catch (error) {
        console.error(error)
      } finally {
        this.isApproving = false
      }
    },

    doApprove() {
      const envelopes = this.selectedRows.map(r => r.data)
      this.approveOrReject(envelopes)
    },

    transformRows(rows) {
      return rows.map(r => {
        let amount = parseFloat(r.data.Amount).toFixed(2)
        let row = {
          NeedsApproval: this.waitingApprovalStatuses.includes(r.data.EnvelopeStatus) ? 'EvedPay Envelope' : '',
          Amount: amount + ' USD'
        }
        return row
      })
    },

    getApprovalUsers(envelopeId) {
      this.api.get(this.api.$.evedpaycard.approvalUsersForEnvelope, {
        params: {
          envelopeId
        }
      })
        .then(response => {
          this.approvalUsersData = response.data
        })
        .catch(error => {
          console.error('There was a problem with the approval users request:', error)
        })
    },

    isUserInApprovalList() {
      const currentUserID = this.$root.getUser.$.user.UserID
      if (Array.isArray(this.approvalUsersData)) {
        return this.approvalUsersData.some(user => user.Id === currentUserID && user.IsCurrentAction)
      }
      return false
    },

    async handleButtonClick(envelopeId) {
      try {
        await this.getApprovalUsers(envelopeId)

        this.showApprovalUsers = true
      } catch (error) {
        console.error('There was a problem handling the button click:', error)
      }
    },

    async getAccountSettings(divisionId) {
      if (this.accountSettings[divisionId]) {
        return
      }

      this.loadingAccountSettings = true
      try {
        const response = await this.api.get(`evedpaycards/accounts/${divisionId}/settings-by-division`)
        if (response.status === 200) {
          this.accountSettings[divisionId] = response.data
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.loadingAccountSettings = false
      }
    },

    singularize(value) {
      return value.endsWith('s') ? value.slice(0, -1) : value
    },

    exportEnvelope() {
      window.open(`${this.api.env}/evedpaycards/envelope/export/${this.selectedRows[0].data.Id}`, '_blank')
    },

    async createAPFile() {
      this.creatingAPFile = true

      let payload = {
        envelopeIds: this.selectedRows.map(r => r.data.Id)
      }
      const response = await this.api.post(this.api.$.evedpaycard.createAPFile, payload)
      if (!response.data) {
        this.creatingAPFile = false
        throw new Error('Error during the request')
      } else {
        this.creatingAPFile = false
        this.refresh()
      }
    },

    async pushToAccountingSystem() {
      this.pushingToAccountingSystem = true

      const response = await this.api.put(`evedpaycards/envelope/push-to-accounting-system`, {
        envelopeIds: this.selectedRows.map(r => r.data.Id)
      })

      if (response.status !== 200) {
        this.pushingToAccountingSystem = false
        throw new Error('Error during the request')
      } else {
        this.pushingToAccountingSystem = false

        this.$notifications.notify({
          message: `The envelope(s) data has been sent to PSL+.`,
          type: 'info',
          placement: { from: 'top', align: 'center' }
        })

        this.refresh()
      }
    },

    rowSelected(event) {
      if (typeof event === 'boolean') {
        this.selectedAll = event
      } else {
        this.selectedRows = event
        if (this.highlightedEnvelopeId.length > 0 && !this.showCreateEnvelope && this.selectedRows.length > 0) {
          this.highlightedEnvelopeId = ''
          localStorage.setItem('row-highlight', '')
          this.redrawRows()
        }
        if (this.selectedRows.length === 1) {
          this.envelope = this.selectedRows[0].data
          this.getApprovalUsers(this.envelope.Id)
          this.isUserInApprovalList()
          this.getAccountSettings(this.envelope.DivisionId)
        }
      }
    },

    envelopeActionDisabled(actionName) {
      return !!this.actionDisabledReason(actionName)
    },

    async getEnvelope(envelopeId, FromDashboard) {
      try {
        const response = await this.api.forms.post(this.api.$.evedpaycard.envelopes, {
          sort: 'CreatedDate',
          dir: 'ASC',
          start: 0,
          limit: 100,
          isLiveGrid: true,
          filter: JSON.stringify({ 'EnvelopeId': envelopeId, 'Screen': FromDashboard ? 'Dashboard' : undefined, searchTerm: '' })
        })

        if (response && response.data) {
          return response.data.Data[0]
        }
      } catch (error) {
        console.error('Error while retrieving envelopes:', error)
      }
    },

    actionDisabledReason(actionName) {
      if (this.selectedRows.length === 0) {
        return cancelTooltip
      }
      if (actionName === 'exportEnvelope') {
        if (this.selectedRows.length !== 1) {
          return 'You can only export one envelope at a time.'
        } else if (this.selectedRows.length === 1) {
          if (this.selectedRows[0].data.EnvelopeStatus === 'Draft') {
            return 'You cannot export draft envelopes.'
          }
          if (this.rejectedStatuses.includes(this.selectedRows[0].data.EnvelopeStatus)) {
            return 'You cannot export rejected envelopes.'
          }
        }
      }

      if (actionName === 'addTransactionNumber') {
        if (this.selectedRows.length === 0) {
          return 'Select an envelope.'
        }

        if (this.selectedRows.length === 1 && this.selectedRows[0].data.TransactionNo !== null) {
          return 'Envelope already has a transaction number.'
        }

        const hasTransactionNo = this.selectedRows.some(row => row.data.TransactionNo !== null)
        const hasOtherStatus = this.selectedRows.some(row =>
          row.data.EnvelopeStatus !== 'Exported - Approved' &&
          row.data.EnvelopeStatus !== 'Exported - Waiting Approval'
        )
        const firstApFileId = this.selectedRows[0].data.ApFileId
        const sameApFileId = this.selectedRows.every(row => row.data.ApFileId === firstApFileId)

        if (hasTransactionNo || hasOtherStatus || !sameApFileId) {
          return 'All selected envelopes must be in the same AP File and without a Transaction Number.'
        }
      }

      if (actionName === 'createAPFile') {
        if (this.creatingAPFile) {
          return 'Creating AP File...'
        }

        if (this.selectedRows.length > 0) {
          for (let row of this.selectedRows) {
            if (row.data.APFile !== '' || row.data.EnvelopeStatus === 'Draft' || this.rejectedStatuses.includes(row.data.EnvelopeStatus)) {
              return 'You can only select envelopes that are not drafts, not rejected, and do not have an AP file.'
            }
          }
          for (let row of this.selectedRows) {
            if (!row.data.CanGenerateAPFileWithoutApproval && row.data.EnvelopeStatus !== 'Open - Approved') {
              return 'One or more envelopes need to be approved first.'
            }
          }
        }
      }

      if (actionName === 'pushToAccountingSystemAction') {
        if (this.pushingToAccountingSystem) {
          return 'Pushing to PSL+...'
        }

        if (this.selectedRows.length > 0) {
          const divisionLabel = this.$root.getUser.settings.Labels.DivisionName
          for (let row of this.selectedRows) {
            let accountSettings = this.accountSettings[row.data.DivisionId]
            if (!accountSettings || !accountSettings.AccountingSystem || accountSettings.AccountingSystem.SyncEvedPayCardEnvelopes !== true) {
              return `"PSL+ Integration - Sync Envelope data" is unchecked on one or more ${divisionLabel}`
            }

            if (!row.data.CardNumberKeyForAccountingSystem) {
              return 'Please first push card to PSL+ or assign the corresponding card number in PSL+ to the existing EVED card.'
            }

            if (row.data.EnvelopeStatus === 'Draft' || this.rejectedStatuses.includes(row.data.EnvelopeStatus)) {
              return 'You can only select envelopes that are not drafts or rejected.'
            }
          }
        }
      }

      if (this.selectedRows.length > 0) {
        for (let row of this.selectedRows) {
          if (!this.waitingApprovalStatuses.includes(row.data.EnvelopeStatus) && (actionName === 'approve' || actionName === 'reject')) {
            return 'You can only select envelopes awaiting approval.'
          }
        }
      }
      if (this.selectedRows.length > 1 && actionName === 'envelope') {
        return 'You can only open one envelope at a time.'
      }
    },

    updateUrlBackToEnvelopesPage() {
      if (this.isFromDashboard && window.parent !== window) {
        window.parent.postMessage({ type: 'LEGACY_GO_TO_DASHBOARD' }, '*')
        return
      }
      const page = this.$route.params.page
      if (backToMap[page]) {
        window.location.href = `${this.api.env}${backToMap[page]}`
      } else {
        window.history.pushState({}, '', '#/d/Programs/>Card/envelopes')
      }
    },

    refresh() {
      this.$refs.grid.refreshGrid()
    },

    redrawRows() {
      this.$refs.grid.gridOptions.api.redrawRows()
    }
  }
}

</script>

<style lang="scss" scoped>
  @import "~@/assets/sass/_variables.scss";

  .grid >>> {
    .ag-row.row-highlight {
      @include theme {
        background-color: #DBE9F9;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/_modules/Programs/Card-envelopes/index.vue